import { type PoolBreakdown, clampNonNegative, formatMoney } from '@paybae/shared'
import { useState } from 'react'

import { Donut, type DonutSegment } from '@/components/Donut'
import { cn } from '@/lib/cn'

/**
 * La dona de compromiso — el elemento que hace memorable a PayBae.
 *
 * Un saldo dice cuánto hay. Esta dona dice qué **forma** tiene ese dinero: qué
 * parte ya tiene dueño y cuál sobra de verdad. Es la diferencia entre «tengo
 * $79,501» y «de esos, $23,599 ya están comprometidos». La primera lectura lleva
 * a gastar de más; la segunda es la que evita el sobregiro.
 *
 * El centro muestra el tramo libre por defecto, y el del segmento que se toque
 * mientras esté seleccionado.
 */

const META = {
  fixed: { label: 'Fijos', color: 'var(--commit-fixed)', dot: 'bg-commit-fixed' },
  cards: { label: 'Tarjetas', color: 'var(--commit-cards)', dot: 'bg-commit-cards' },
  savings: { label: 'Ahorro', color: 'var(--commit-savings)', dot: 'bg-commit-savings' },
  free: { label: 'Libre', color: 'var(--commit-free)', dot: 'bg-commit-free' },
} as const

type Clave = keyof typeof META

export function CommitmentDonut({
  breakdown,
  className,
}: {
  breakdown: PoolBreakdown
  className?: string
}) {
  const [activo, setActivo] = useState<string | null>(null)

  // El tramo libre se acota a cero: un sobregiro no es una porción negativa de
  // la dona, es la ausencia de porción. La alerta se da con el número, no aquí.
  const libre = clampNonNegative(breakdown.freeCents)

  const valores: Record<Clave, number> = {
    fixed: breakdown.committedFixedCents,
    cards: breakdown.committedCardsCents,
    savings: breakdown.committedSavingsCents,
    free: libre,
  }

  const segments: DonutSegment[] = (Object.keys(META) as Clave[]).map((key) => ({
    key,
    label: META[key].label,
    value: valores[key],
    color: META[key].color,
  }))

  const total = segments.reduce((s, seg) => s + seg.value, 0)
  const seleccionado = (activo as Clave | null) ?? null

  const centroValor = seleccionado ? valores[seleccionado] : libre
  const centroEtiqueta = seleccionado
    ? `${META[seleccionado].label} · ${porcentaje(valores[seleccionado], total)}`
    : 'Libre para gastar'

  return (
    <div className={cn('flex items-center gap-4', className)}>
      <Donut
        segments={segments}
        centerValue={formatMoney(centroValor, { hideZeroCents: true })}
        centerLabel={centroEtiqueta}
        onSelect={setActivo}
      />

      <ul className="flex min-w-0 flex-1 flex-col gap-2">
        {segments.map((segment) => {
          const atenuado = activo !== null && activo !== segment.key
          return (
            <li
              key={segment.key}
              className={cn('flex items-baseline gap-2 transition-opacity', atenuado && 'opacity-40')}
            >
              <span
                aria-hidden="true"
                className={cn('size-2 shrink-0 translate-y-[-1px] rounded-full', META[segment.key as Clave].dot)}
              />
              <span className="line-clamp-2 text-[13px] leading-tight text-ink-soft">{segment.label}</span>
              <span data-money className="ml-auto text-[13px] font-semibold text-ink">
                {formatMoney(segment.value, { hideZeroCents: true })}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function porcentaje(valor: number, total: number): string {
  if (total <= 0) return '0%'
  return `${Math.round((valor / total) * 100)}%`
}
